import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { BORDER_COLORS, COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { FONT_CSS } from "../../../constants/design/fontSize";
import { SPACING } from "../../../constants/design/spacing";

export const FAMILY_BAR_HEIGHT = 48;

type Variant = "sub" | "master";

const variantColors = (variant: Variant) =>
  variant === "sub"
    ? { text: COLORS.orange.one, bg: COLORS.orange.five, border: COLORS.orange.three }
    : { text: COLORS.blue.one, bg: COLORS.blue.two, border: COLORS.blue.three };

export const RelationPill = styled.span<{ variant: Variant; roleLabel?: string }>`
  ${FONT_CSS.label.two};
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.one}px;
  flex-shrink: 0;
  max-width: 220px;
  overflow: hidden;
  padding: 2px ${SPACING.two}px 2px 2px;
  border-radius: 6px;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-transform: none;
  cursor: pointer;
  color: ${({ variant }) => variantColors(variant).text};
  background: ${({ variant }) => variantColors(variant).bg};
  border: 1px solid ${({ variant }) => variantColors(variant).border};

  // Solid role label. A pseudo-element, because dashboard rows with an Airtable name recolour
  // every first child on hover with !important.
  ${({ roleLabel, variant }) =>
    roleLabel &&
    css`
      &::before {
        ${FONT_CSS.label.three};
        content: "${roleLabel}";
        padding: 2px ${SPACING.one}px;
        border-radius: 4px;
        font-weight: 600;
        letter-spacing: 0.4px;
        color: ${COLORS.black.one};
        background: ${variantColors(variant).text};
      }
    `}
`;

export const FamilyBarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.twoHalf}px;
  height: ${FAMILY_BAR_HEIGHT}px;
  padding: 0 ${SPACING.three}px 0 ${SPACING.two}px;
  border-top: 1px solid ${BORDER_COLORS.one};
  background: black;
  min-width: 0;
`;

export const RoleBadge = styled.span<{ variant: Variant }>`
  ${FONT_CSS.label.three};
  flex-shrink: 0;
  padding: ${SPACING.one}px ${SPACING.two}px;
  border-radius: 6px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ variant }) => variantColors(variant).text};
  background: ${({ variant }) => variantColors(variant).bg};
  border: 1px solid ${({ variant }) => variantColors(variant).border};
`;

export const FamilyChips = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: thin;
`;

export const FamilySeparator = styled.span`
  ${FONT_CSS.body.two};
  color: ${TEXT_COLORS.three};
  flex-shrink: 0;
`;

export const FamilyChip = styled(Link)<{ variant: Variant; isCurrent?: boolean; isArchived?: boolean }>`
  ${FONT_CSS.label.two};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 ${SPACING.twoHalf}px;
  border-radius: 8px;
  white-space: nowrap;
  text-decoration: none;
  color: ${TEXT_COLORS.one};
  background: ${COLORS.white.five};
  border: 1px solid ${BORDER_COLORS.two};
  opacity: ${({ isArchived }) => (isArchived ? 0.5 : 1)};
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    color: ${TEXT_COLORS.one};
    border-color: ${COLORS.white.four};
  }

  ${({ isCurrent, variant }) =>
    isCurrent &&
    css`
      background: ${variantColors(variant).bg};
      border-color: ${variantColors(variant).text};
      cursor: default;

      &:hover {
        border-color: ${variantColors(variant).text};
      }
    `}
`;

export const ChipRole = styled.span`
  ${FONT_CSS.label.three};
  color: ${COLORS.blue.one};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

export const ChipName = styled.span`
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChipAddress = styled.span`
  color: ${TEXT_COLORS.three};
  font-family: monospace;
`;

export const ChipCount = styled.span`
  ${FONT_CSS.label.three};
  min-width: 18px;
  padding: 2px ${SPACING.one}px;
  border-radius: 9px;
  text-align: center;
  color: ${TEXT_COLORS.one};
  background: ${COLORS.white.three};
`;

export const FamilyTotals = styled.div`
  ${FONT_CSS.label.two};
  display: flex;
  align-items: center;
  gap: ${SPACING.three}px;
  flex-shrink: 0;
  color: ${TEXT_COLORS.two};
  white-space: nowrap;

  strong {
    font-weight: normal;
    color: ${TEXT_COLORS.one};
  }
`;

export const FamilyViewButton = styled.button.attrs({ type: "button" })`
  ${FONT_CSS.label.two};
  flex-shrink: 0;
  padding: 6px ${SPACING.twoHalf}px;
  border-radius: 8px;
  white-space: nowrap;
  color: ${COLORS.blue.one};
  background: ${COLORS.blue.two};
  border: 1px solid ${COLORS.blue.three};

  &:hover {
    border-color: ${COLORS.blue.one};
  }
`;

export const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.one}px;
  max-height: 320px;
  overflow-y: auto;
`;

export const TooltipHeader = styled.div`
  color: ${TEXT_COLORS.two};
  margin-bottom: ${SPACING.one}px;
`;

export const TooltipRow = styled(Link)`
  display: grid;
  grid-template-columns: minmax(80px, 1fr) auto auto auto;
  gap: ${SPACING.three}px;
  color: ${TEXT_COLORS.one};
  text-decoration: none;

  &:hover {
    color: ${COLORS.blue.one};
  }

  > span:last-child {
    color: ${TEXT_COLORS.two};
    font-family: monospace;
  }
`;

export const TooltipStat = styled.span`
  color: ${TEXT_COLORS.two};
  text-align: right;
`;

export const TooltipFooter = styled(Link)`
  margin-top: ${SPACING.one}px;
  padding-top: ${SPACING.one}px;
  border-top: 1px solid ${BORDER_COLORS.one};
  color: ${COLORS.blue.one};
  text-decoration: none;

  &:hover {
    color: ${COLORS.blue.one};
    text-decoration: underline;
  }
`;

export const SubaccountTable = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 0 ${({ isMobile }) => (isMobile ? SPACING.two : SPACING.three)}px ${SPACING.three}px;
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 6px;
`;

export const SubaccountTableRow = styled(Link)`
  ${FONT_CSS.label.two};
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: ${SPACING.two}px;
  padding: ${SPACING.two}px;
  color: ${TEXT_COLORS.one};
  text-decoration: none;

  &:not(:last-child) {
    border-bottom: 1px solid ${BORDER_COLORS.two};
  }

  &:hover {
    background: ${BORDER_COLORS.three};
  }
`;

export const SubaccountName = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  > span:last-child {
    color: ${TEXT_COLORS.two};
    font-family: monospace;
  }
`;

export const MutedText = styled.span`
  color: ${TEXT_COLORS.two};
`;
